// Components
import RenderSimOperator from '../../simOperator/RenderSimOperator';
import RenderDateWithFromTime from '@/common-utils/components/renders/date/RenderDateWithFromTime';
import Entity from '@/common-utils/components/renders/entity/Entity';
import RenderDataUnit from '../../dataUnit/RenderDataUnit';
import CustomPayRenderDataUnit from '../../dataUnit/CustomPayRenderDataUnit';
import RenderSimUpdateTooltip from '../../simOperator/RenderSimUpdateTooltip';
import DropdownSimCardActionsRow from '../../dropdown/DropdownSimCardActionsRow';
import ColumnSelection from '@/common-utils/components/renders/dataTable/column/ColumnSelection';
import DetailsButton from '@/common-utils/components/renders/detailsButton/DetailsButton';
import { Badge } from '@/common-utils/components/ui/badge';
// Common
import simCardFields from '../../../fields/simCardFields';
import SIM_CARDS_STATUS from '../../../enums/SimCardStatus';
import RouteUrls from '@/app/root/enums/RouteUrls';
import generateColumnDefByField from '@/common-utils/functions/generateColumnDefByField';
import getIccidWithoutPrefix from '@/common-utils/functions/getIccidWithoutPrefix';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import Permissions from '@/app/permissions/Permissions';
import SIM_CARDS_TABS from '@/app/root/enums/tabs/SimCardsTabs';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

export const columnsSimCardsId = 'simCards';

export const columnsSimCards = (headerPackage, isCustomPay) => {

    return ([
        // ColumnSelection
        headerPackage.selectionMode && ColumnSelection(columnsSimCardsId, headerPackage.singleSelection, headerPackage.maxPicks),
        generateColumnDefByField({
            field: simCardFields.DETAILS,
            headerPackage: headerPackage,
            customCellFn: ({ row }) => (
                <DetailsButton
                    pathParams={{ simCardId: row.original.uuid }}
                    baseUrl={RouteUrls.SIM_CARDS_DETAILS.url}
                    urlParams={{ view: SIM_CARDS_TABS.SIM_CARDS.name }}
                    requiredPermissions={[Permissions.SIM_READ.name]}
                />
            ),
            show: !headerPackage.selectionMode
        }),
        generateColumnDefByField({
            field: simCardFields.UUID,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                return (
                    <DropdownSimCardActionsRow
                        simCard={cell.row.original}
                        setLaunchRefetch={headerPackage.setLaunchRefetch}
                    />
                );
            },
            show: !headerPackage.selectionMode
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.ICCID,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const simNumber = cell.getValue(simCardFields.SIM_CARD.ICCID.key);
                const nextIccid = getObjectAttributeFromStringKey(cell.row.original,
                    generateNestedFieldAccessor(simCardFields.SIM_CARD.NEXT_ICCID));
                const iccidOperators = getObjectAttributeFromStringKey(cell.row.original,
                    generateNestedFieldAccessor(simCardFields.SIM_CARD.ICCID_OPERATOR));
                return (
                    simNumber &&
                    <div className='flex'>
                        <div className='flex flex-col mr-1'>
                            <small className='whitespace-nowrap'>{getIccidWithoutPrefix(simNumber)}</small>
                            <RenderSimOperator iccidOperators={iccidOperators} />
                        </div>
                        {nextIccid &&
                            <RenderSimUpdateTooltip
                                data={nextIccid}
                            />
                        }
                    </div>
                );
            }
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.STATUS,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const status = SIM_CARDS_STATUS[cell.getValue(simCardFields.SIM_CARD.STATUS.key)];
                return (
                    status &&
                    <Badge entity={status.entity} namespace={status.namespace} variant={status.variant} />
                );
            }
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.POS_TERMINAL.LAST_GPRS_CALL,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(simCardFields.SIM_CARD.POS_TERMINAL.LAST_GPRS_CALL.key)} />
            )
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.DATA,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const dataUnit = getObjectAttributeFromStringKey(cell.row.original,
                    generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA_UNIT));
                const tariffName = getObjectAttributeFromStringKey(cell.row.original,
                    generateNestedFieldAccessor(simCardFields.SIM_CARD.TARIFF_NAME));
                const nextOffer = getObjectAttributeFromStringKey(cell.row.original,
                    generateNestedFieldAccessor(simCardFields.SIM_CARD.NEXT_OFFER));
                const cellValue = cell.getValue(simCardFields.SIM_CARD.DATA.key);

                return (
                    cellValue ?
                        isCustomPay ?
                            <CustomPayRenderDataUnit cellData={cellValue} dataUnit={dataUnit} />
                            :
                            <RenderDataUnit cellData={cellValue} dataUnit={dataUnit} tariffName={tariffName} nextOffer={nextOffer} />
                        :
                        '-'
                );
            }
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.DATA_USAGE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const cellValue = cell.getValue(simCardFields.SIM_CARD.DATA_USAGE.key);
                const valueMo = cellValue ? (cellValue.dataConsumption / 1048576).toFixed(3) : null;
                return (
                    valueMo ?
                        <div>
                            {valueMo} <Entity entity='units.MB' namespace={nsSimCard} />
                        </div>
                        : null);
            }
        }),
        generateColumnDefByField({
            field: simCardFields.CLIENT.LEGAL_NAME,
            headerPackage: headerPackage,
            isLongText: true
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.LABEL,
            headerPackage: headerPackage,
            isLongText: true
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.POS_TERMINAL.SERIAL_NUMBER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.POS_TERMINAL.MANUFACTURER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.UPDATED_AT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(simCardFields.SIM_CARD.UPDATED_AT.key)} />
            )
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.ACTIVATION_DATE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(simCardFields.SIM_CARD.ACTIVATION_DATE.key)} />
            )
        }),
        generateColumnDefByField({
            field: simCardFields.SIM_CARD.CREATED_AT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(simCardFields.SIM_CARD.CREATED_AT.key)} />
            )
        }),

        generateColumnDefByField({
            field: simCardFields.SIM_CARD.CANCELLATION_DATE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(simCardFields.SIM_CARD.CANCELLATION_DATE.key)} />
            )
        })
    ]).filter(col => !!col);
};